/**
 * Learn-Mode overlay geometry — pure functions, viewport (position: fixed)
 * coordinates throughout. Lifted from the medspa staff overlay, whose clamp
 * keeps every badge and popover on-screen no matter how large or how far
 * scrolled its anchor is.
 */

/** Width of the popover (matches Tailwind `w-72`). */
export const POPOVER_WIDTH = 288;
/** Badges and popovers stay at least this far from the viewport edge. */
export const VIEWPORT_MARGIN = 8;
/** Floating badge size (Tailwind `w-5 h-5`). */
export const BADGE_SIZE = 20;
/** Floating badge overhangs its anchor's top-right corner by this much. */
export const BADGE_OVERHANG = 8;
/** Vertical gap between a badge and its popover. */
export const POPOVER_GAP = 6;

export interface Point {
  top: number;
  left: number;
}

export interface RectLike {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface Viewport {
  width: number;
  height: number;
}

/**
 * Clamp `value` into [min, max]. When the span is smaller than the element
 * (max < min) pin to `min`, so the element stays anchored to the leading edge
 * rather than jumping.
 */
export function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

/** Popover width for a viewport: `POPOVER_WIDTH`, shrunk to fit narrow screens. */
export function popoverWidthFor(viewport: Viewport): number {
  return Math.max(0, Math.min(POPOVER_WIDTH, viewport.width - 2 * VIEWPORT_MARGIN));
}

/**
 * Floating badge position: the anchor's top-right corner, nudged out by
 * `BADGE_OVERHANG`, clamped so the badge never leaves the screen.
 */
export function floatingBadgePosition(anchor: RectLike, viewport: Viewport): Point {
  return {
    top: clamp(anchor.top - BADGE_OVERHANG, VIEWPORT_MARGIN, viewport.height - BADGE_SIZE - VIEWPORT_MARGIN),
    left: clamp(anchor.right - BADGE_OVERHANG, VIEWPORT_MARGIN, viewport.width - BADGE_SIZE - VIEWPORT_MARGIN),
  };
}

/**
 * Popover position next to its BADGE (never the anchor's far corner — a large
 * anchor such as a whole calendar would otherwise throw the popover hundreds of
 * px away). Right-aligned to the badge's right edge and clamped horizontally;
 * placed below the badge, flipped above when it would overflow the bottom, and
 * pinned inside the viewport when neither side fits.
 *
 * `popoverHeight` is 0 before the first measure — the popover is then placed
 * below and re-positioned once it has a real height.
 */
export function popoverPosition(badge: RectLike, popoverHeight: number, viewport: Viewport): Point {
  const width = popoverWidthFor(viewport);
  const left = clamp(badge.right - width, VIEWPORT_MARGIN, viewport.width - width - VIEWPORT_MARGIN);
  let top = badge.bottom + POPOVER_GAP;
  if (popoverHeight > 0 && top + popoverHeight > viewport.height - VIEWPORT_MARGIN) {
    const above = badge.top - POPOVER_GAP - popoverHeight;
    top =
      above >= VIEWPORT_MARGIN
        ? above
        : clamp(viewport.height - popoverHeight - VIEWPORT_MARGIN, VIEWPORT_MARGIN, viewport.height - VIEWPORT_MARGIN);
  }
  return { top, left };
}
